import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Star, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AllergenSelector } from './AllergenSelector';
import { SpiceLevelSelector } from './SpiceLevelSelector';
import { CuisineTagSelector } from './CuisineTagSelector';
import type { 
  AllergenInfo, 
  SpiceLevel, 
  CuisineTag,
  EnhancedDietTypeFormData 
} from '@/types';

interface EnhancedDietTypeSelectorProps {
  value: Partial<EnhancedDietTypeFormData>;
  onChange: (data: Partial<EnhancedDietTypeFormData>) => void;
  errors?: Record<string, string>;
  onFieldFocus?: () => void;
  className?: string;
}

const defaultAllergens: AllergenInfo = {
  nuts: false,
  dairy: false,
  gluten: false,
  soy: false,
  eggs: false,
  shellfish: false,
  sesame: false,
  mustard: false,
  custom: [],
  mayContain: [],
};

// Remove the unused defaultSpiceLevel constant

/**
 * EnhancedDietTypeSelector - Comprehensive diet type selection system
 * Combines basic diet types, allergens, spice levels, and cuisine tags
 */
export const EnhancedDietTypeSelector: React.FC<EnhancedDietTypeSelectorProps> = ({
  value,
  onChange,
  onFieldFocus,
  className,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    allergens: false,
    spiceLevel: false,
    cuisineTags: false,
    specialFlags: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAllergensChange = (allergens: AllergenInfo) => {
    onChange({ ...value, allergens });
  };

  const handleSpiceLevelChange = (spiceLevel: SpiceLevel) => {
    onChange({ ...value, spiceLevel });
  };

  const handleCuisineTagsChange = (cuisineTags: CuisineTag[]) => {
    onChange({ ...value, cuisineTags });
  };

  const handleSignatureDishChange = (isSignatureDish: boolean) => {
    const updatedValue = { ...value, isSignatureDish };
    onChange(updatedValue);
  };

  const handleChefSpecialChange = (isChefSpecial: boolean) => {
    const updatedValue = { ...value, isChefSpecial };
    onChange(updatedValue);
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Enhanced Sections */}
      <div className="space-y-4">
        {/* Allergen Information */}
        <div className="border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('allergens')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg"
          >
            <span className="text-sm font-medium text-gray-700">Allergen Information</span>
            {expandedSections.allergens ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          <AnimatePresence>
            {expandedSections.allergens && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 pb-4"
              >
                <AllergenSelector
                  value={value.allergens || defaultAllergens}
                  onChange={handleAllergensChange}
                  onFieldFocus={onFieldFocus}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Spice Level */}
        <div className="border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('spiceLevel')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg"
          >
            <span className="text-sm font-medium text-gray-700">Spice Level</span>
            {expandedSections.spiceLevel ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          <AnimatePresence>
            {expandedSections.spiceLevel && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 pb-4"
              >
                <SpiceLevelSelector
                  value={value.spiceLevel || { level: 'none', preparationNotes: '' }}
                  onChange={handleSpiceLevelChange}
                  onFieldFocus={onFieldFocus}
                  showAsUnselected={!value.spiceLevel}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Cuisine Tags */}
        <div className="border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('cuisineTags')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg"
          >
            <span className="text-sm font-medium text-gray-700">Cuisine Types</span>
            {expandedSections.cuisineTags ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          <AnimatePresence>
            {expandedSections.cuisineTags && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 pb-4"
              >
                <CuisineTagSelector
                  value={value.cuisineTags || []}
                  onChange={handleCuisineTagsChange}
                  onFieldFocus={onFieldFocus}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Special Flags */}
        <div className="border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('specialFlags')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg"
          >
            <span className="text-sm font-medium text-gray-700">Special Highlights</span>
            {expandedSections.specialFlags ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          <AnimatePresence>
            {expandedSections.specialFlags && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 pb-4 space-y-4"
              >
                {/* Signature Dish */}
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.isSignatureDish || false}
                    onChange={(e) => handleSignatureDishChange(e.target.checked)}
                    onFocus={onFieldFocus}
                    className="w-4 h-4 checkbox-primary border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  />
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-primary" />
                    <span className="text-sm text-gray-700">Signature Dish</span>
                  </div>
                </label>

                {/* Chef Special */}
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.isChefSpecial || false}
                    onChange={(e) => handleChefSpecialChange(e.target.checked)}
                    onFocus={onFieldFocus}
                    className="w-4 h-4 checkbox-primary border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  />
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-primary" />
                    <span className="text-sm text-gray-700">Chef's Special</span>
                  </div>
                </label>

                <p className="text-xs text-gray-500 mt-2">
                  Special highlights help customers identify recommended dishes
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Summary Preview */}
      {(value.dietType || value.allergens || value.spiceLevel || value.cuisineTags?.length) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-green-50 rounded-lg p-4 border border-green-200"
        >
          <h4 className="text-sm font-medium text-green-800 mb-2">Diet Information Summary</h4>
          <div className="space-y-1 text-sm text-green-700">
            {value.dietType && <p>• Diet Type: {value.dietType}</p>}
            {value.spiceLevel?.level && value.spiceLevel.level !== 'none' && (
              <p>• Spice Level: {value.spiceLevel.level}</p>
            )}
            {value.cuisineTags && value.cuisineTags.length > 0 && (
              <p>• Cuisine: {value.cuisineTags.map(tag => tag.type).join(', ')}</p>
            )}
            {value.isSignatureDish && <p>• Signature Dish</p>}
            {value.isChefSpecial && <p>• Chef's Special</p>}
          </div>
        </motion.div>
      )}
    </div>
  );
};